import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { useAdminToast } from "../AdminToast";
import { adminFetch, BACKEND_URL } from "../adminApi";
import "./admin-products.css";

/**
 * RAdminProducts — /admin/products (redesign of pages/AdminProductsPage.js).
 *
 * Visual ground truth: design-ref/admin/Products.html (filter bar card,
 * 4-column product card grid with FEATURED / OUT OF STOCK flags and the
 * Active/Inactive pill, purple pagination).
 *
 * Functional ground truth: the legacy AdminProductsPage —
 *   - GET /api/products?limit&offset(&category&search&featured&status) (public)
 *   - GET /api/products/count?same-filters (public)
 *   - GET /api/categories (public, category filter options)
 *   - GET /api/admin/products/{id} (auth — preview)
 *   - PATCH /api/admin/products/{id}/toggle-status (auth — Active pill)
 *   - DELETE /api/admin/products/{id} (auth — delete confirm)
 *   - 20 per page, 5-button page window, Clear Filters resets everything
 * Create/Edit moved from the legacy in-page ProductForm modal to dedicated
 * routes: cards link to /admin/products/:id/edit, the header CTA goes to
 * /admin/products/new (RAdminProductForm screen).
 */

const PRODUCTS_PER_PAGE = 20;
const PLACEHOLDER_IMG =
  "https://placehold.co/300x300/e5e7eb/6b7280?text=No+Image";

const audFormatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
});

/* Legacy formatPrice semantics: 0 / missing price => "Price on request". */
const formatPrice = (price) => {
  const numPrice = parseFloat(price || 0);
  return numPrice > 0 ? audFormatter.format(numPrice) : "Price on request";
};

const productId = (product) => product._id || product.id;

/* Bridges the toast api (provided by AdminShell, inside its own tree) up to
   the component that owns the state/handlers. Renders nothing. */
const ToastBinder = ({ onToast }) => {
  const toast = useAdminToast();
  useEffect(() => {
    onToast(toast);
  }, [toast, onToast]);
  return null;
};

/* ---- inline icons (design-style stroked SVGs) ---- */
const RefreshIcon = (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="#2a2018"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 11a8 8 0 10-2 6" />
    <path d="M20 4v5h-5" />
  </svg>
);

const EyeIcon = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const TrashIcon = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const STOCK_STATUS_CHIP = {
  instock: { className: "adm-chip adm-chip--teal", label: "In Stock" },
  outofstock: { className: "adm-chip adm-chip--red", label: "Out of Stock" },
  onbackorder: { className: "adm-chip adm-chip--amber", label: "On Backorder" },
};

const RAdminProducts = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* toast handle (AdminShell mounts the provider around our children) */
  const toastRef = useRef(null);
  const bindToast = useCallback((toast) => {
    toastRef.current = toast;
  }, []);

  /* ---- shared filter params (identical to the legacy page) ---- */
  const appendFilters = useCallback(
    (params) => {
      if (categoryFilter && categoryFilter !== "all")
        params.append("category", categoryFilter);
      if (searchTerm) params.append("search", searchTerm);
      if (featuredFilter && featuredFilter !== "all")
        params.append("featured", featuredFilter);
      if (statusFilter && statusFilter !== "all")
        params.append("status", statusFilter);
    },
    [categoryFilter, searchTerm, featuredFilter, statusFilter]
  );

  /* GET /api/products (+ /count) — public endpoints, no auth header,
     exactly like the legacy page. */
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        limit: PRODUCTS_PER_PAGE.toString(),
        offset: ((currentPage - 1) * PRODUCTS_PER_PAGE).toString(),
      });
      appendFilters(params);

      const response = await fetch(`${BACKEND_URL}/api/products?${params}`);
      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }
      const productsData = await response.json();
      setProducts(productsData);

      // Total count for pagination (failures are silently ignored — legacy).
      const countParams = new URLSearchParams();
      appendFilters(countParams);
      const countResponse = await fetch(
        `${BACKEND_URL}/api/products/count?${countParams}`
      );
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalProducts(countData.count);
      }
    } catch (err) {
      if (toastRef.current) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, appendFilters]);

  /* GET /api/categories — populates the category filter (errors only logged,
     legacy behaviour). */
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(categoriesData);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  }, []);

  /* Refetch on page / filter change (legacy effect; statusFilter is included
     here — the legacy page omitted it from the deps by mistake). */
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  /* PATCH /api/admin/products/{id}/toggle-status — Active/Inactive pill.
     Optimless legacy flow: wait for the response, then patch the local list
     with the returned status. */
  const handleToggleProductStatus = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await adminFetch(
        `/api/admin/products/${product._id}/toggle-status`,
        { method: "PATCH", navigate }
      );
      if (!response.ok) {
        throw new Error("Failed to toggle product status");
      }
      const result = await response.json();

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p._id === product._id ? { ...p, status: result.status } : p
        )
      );
      console.log(
        `✅ Product ${product.name} status changed to: ${result.status}`
      );
      if (toastRef.current)
        toastRef.current.success(
          result.status === "publish"
            ? "Product set to Active"
            : "Product set to Inactive"
        );
    } catch (err) {
      console.error("Error toggling product status:", err);
      if (!err.isAuthError && toastRef.current)
        toastRef.current.error(err.message);
    }
  };

  /* GET /api/admin/products/{id} — full details for the preview modal
     (legacy handleViewProduct). */
  const handleViewProduct = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await adminFetch(`/api/admin/products/${product._id}`, {
        navigate,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch product details");
      }
      const productDetails = await response.json();
      setPreviewProduct(productDetails);
    } catch (err) {
      if (!err.isAuthError && toastRef.current)
        toastRef.current.error(err.message);
    }
  };

  const handleDeleteProduct = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm(product);
  };

  /* DELETE /api/admin/products/{id} — then refresh the list (legacy). */
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      const response = await adminFetch(
        `/api/admin/products/${deleteConfirm._id}`,
        { method: "DELETE", navigate }
      );
      if (!response.ok) {
        throw new Error("Failed to delete product");
      }
      setDeleteConfirm(null);
      if (toastRef.current) toastRef.current.success("Product deleted");
      fetchProducts(); // Refresh the list
    } catch (err) {
      if (!err.isAuthError && toastRef.current)
        toastRef.current.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setFeaturedFilter("all");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const closePreview = () => setPreviewProduct(null);

  /* Escape closes the preview modal (matches the design's modal pattern). */
  useEffect(() => {
    if (!previewProduct) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setPreviewProduct(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [previewProduct]);

  const hasActiveFilters =
    Boolean(searchTerm) ||
    (categoryFilter && categoryFilter !== "all") ||
    (featuredFilter && featuredFilter !== "all") ||
    (statusFilter && statusFilter !== "all");

  const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);

  /* 5-button page window — logic copied from the legacy page. */
  const pageWindow = (() => {
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  })();

  const showingFrom = (currentPage - 1) * PRODUCTS_PER_PAGE + 1;
  const showingTo = Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts);

  /* ---- preview modal helpers ---- */
  const previewImage =
    previewProduct && previewProduct.images && previewProduct.images.length > 0
      ? previewProduct.images[0].src
      : PLACEHOLDER_IMG;
  const previewStockChip = previewProduct
    ? STOCK_STATUS_CHIP[previewProduct.stock_status]
    : null;
  const previewHasPhysical =
    previewProduct &&
    (previewProduct.weight ||
      (previewProduct.dimensions &&
        (previewProduct.dimensions.length ||
          previewProduct.dimensions.width ||
          previewProduct.dimensions.height)));

  return (
    <AdminShell
      active="products"
      pageClass="pg-admin-products"
      eyebrow="Commerce"
      title="Manage Products"
      subtitle={`${totalProducts} total products`}
      actions={
        <>
          <button
            type="button"
            className="adm-btn"
            onClick={() => navigate("/admin/products/new")}
          >
            + Add New Product
          </button>
          <button
            type="button"
            className={"adm-iconbtn pr-refresh" + (loading ? " is-spinning" : "")}
            onClick={fetchProducts}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            {RefreshIcon}
          </button>
        </>
      }
    >
      <div className="pg-admin-products">
        <ToastBinder onToast={bindToast} />

        {/* ---- filter bar ---- */}
        <div className="adm-card adm-card--flush">
          <div className="adm-filterbar">
            <input
              className="adm-input adm-input--search"
              placeholder="Search name, SKU, description…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="adm-select adm-select--filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="all">All categories</option>
              {categories.map((category) => (
                <option
                  key={category._id || category.slug || category.name}
                  value={category.slug || category.name}
                >
                  {category.name}
                </option>
              ))}
            </select>
            <select
              className="adm-select adm-select--filter"
              value={featuredFilter}
              onChange={(e) => setFeaturedFilter(e.target.value)}
              aria-label="Filter by featured"
            >
              <option value="all">Featured: All</option>
              <option value="true">Featured Only</option>
              <option value="false">Not Featured</option>
            </select>
            <select
              className="adm-select adm-select--filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">Status: All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              type="button"
              className="adm-btn adm-btn--ghost"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="adm-gap" />

        {/* ---- grid / loading / empty ---- */}
        {loading ? (
          <div className="adm-loading">
            <div className="adm-spinner" />
            Loading products…
          </div>
        ) : products.length === 0 ? (
          <div className="adm-empty">
            <div className="adm-empty-icon">📦</div>
            <div className="adm-empty-title">No products found</div>
            <div className="adm-empty-sub">
              {hasActiveFilters
                ? "Try adjusting your filters"
                : "Add new products using the 'Add New Product' button above"}
            </div>
          </div>
        ) : (
          <>
            <div className="adm-grid4">
              {products.map((product) => {
                const pid = productId(product);
                const primaryImage =
                  product.images && product.images.length > 0
                    ? product.images[0].src
                    : PLACEHOLDER_IMG;
                const isActive = product.status === "publish";

                return (
                  <Link
                    key={pid}
                    to={`/admin/products/${pid}/edit`}
                    className="pr-card"
                  >
                    <div className="pr-media">
                      <img
                        src={primaryImage}
                        alt={product.name}
                        onError={(e) => {
                          e.target.src = PLACEHOLDER_IMG;
                        }}
                      />
                      <div className="pr-flags">
                        {product.featured && (
                          <span className="pr-flag">FEATURED</span>
                        )}
                        {product.stock_status === "outofstock" && (
                          <span className="pr-flag pr-flag--oos">
                            OUT OF STOCK
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={
                          "pr-toggle" + (isActive ? "" : " pr-toggle--off")
                        }
                        onClick={(e) => handleToggleProductStatus(e, product)}
                        title={
                          isActive
                            ? "Active - Click to deactivate"
                            : "Inactive - Click to activate"
                        }
                      >
                        {isActive ? "Active" : "Inactive"}
                      </button>
                      <div className="pr-actions">
                        <button
                          type="button"
                          className="pr-actbtn"
                          title="Preview"
                          aria-label={`Preview ${product.name}`}
                          onClick={(e) => handleViewProduct(e, product)}
                        >
                          {EyeIcon}
                        </button>
                        <button
                          type="button"
                          className="pr-actbtn pr-actbtn--danger"
                          title="Delete"
                          aria-label={`Delete ${product.name}`}
                          onClick={(e) => handleDeleteProduct(e, product)}
                        >
                          {TrashIcon}
                        </button>
                      </div>
                    </div>
                    <div className="pr-body">
                      <div className="pr-name">{product.name}</div>
                      {product.sku && (
                        <div className="pr-sku">SKU: {product.sku}</div>
                      )}
                      <div className="pr-meta">
                        <span className="pr-price">
                          {formatPrice(product.price || product.regular_price)}
                        </span>
                        {product.stock_quantity != null && (
                          <span className="pr-stock">
                            {product.stock_quantity} in stock
                          </span>
                        )}
                      </div>
                      {product.categories && product.categories.length > 0 && (
                        <div className="pr-cats">
                          {product.categories.slice(0, 2).map((category) => (
                            <span
                              key={category.id || category.name}
                              className="pr-cat"
                            >
                              {category.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* ---- pagination (20/page, purple active, 5-page window) ---- */}
            {totalPages > 1 && (
              <div className="adm-pagination">
                <span className="adm-page-info">
                  Showing {showingFrom}–{showingTo} of {totalProducts}
                </span>
                <div className="adm-pages">
                  <button
                    type="button"
                    className="adm-pagebtn"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {pageWindow.map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      className={
                        "adm-pagebtn" +
                        (pageNum === currentPage ? " is-active" : "")
                      }
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="adm-pagebtn"
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ---- product preview modal (legacy ProductPreview, design skin) ---- */}
        {previewProduct && (
          <div className="adm-modal-overlay" onClick={closePreview}>
            <div
              className="adm-modal adm-modal--wide pr-preview"
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="adm-modal-head">
                <div className="adm-modal-title">Product Preview</div>
                <button
                  type="button"
                  className="adm-modal-close"
                  onClick={closePreview}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="pr-preview-grid">
                <div>
                  <div className="pr-preview-media">
                    <img
                      src={previewImage}
                      alt={previewProduct.name}
                      onError={(e) => {
                        e.target.src = PLACEHOLDER_IMG;
                      }}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  {previewProduct.images &&
                    previewProduct.images.length > 1 && (
                      <div className="pr-preview-thumbs">
                        {previewProduct.images
                          .slice(1, 5)
                          .map((image, index) => (
                            <span key={index} className="pr-thumb">
                              <img
                                src={image.src}
                                alt={`${previewProduct.name} ${index + 2}`}
                                onError={(e) => {
                                  e.target.src = PLACEHOLDER_IMG;
                                }}
                                loading="lazy"
                                decoding="async"
                              />
                            </span>
                          ))}
                      </div>
                    )}
                </div>

                <div className="pr-preview-info">
                  <div className="pr-preview-namerow">
                    <div className="pr-preview-name">{previewProduct.name}</div>
                    {previewProduct.featured && (
                      <span className="adm-chip adm-chip--amber">Featured</span>
                    )}
                  </div>
                  {previewProduct.sku && (
                    <div className="pr-sku">SKU: {previewProduct.sku}</div>
                  )}

                  <div className="pr-preview-pricerow">
                    <span className="pr-preview-price">
                      {formatPrice(
                        previewProduct.price || previewProduct.regular_price
                      )}
                    </span>
                    {previewProduct.sale_price &&
                      parseFloat(previewProduct.sale_price) > 0 && (
                        <span className="pr-preview-was">
                          {formatPrice(previewProduct.regular_price)}
                        </span>
                      )}
                  </div>

                  <div className="pr-preview-stockrow">
                    {previewStockChip && (
                      <span className={previewStockChip.className}>
                        {previewStockChip.label}
                      </span>
                    )}
                    {previewProduct.stock_quantity != null && (
                      <span className="pr-stock">
                        {previewProduct.stock_quantity} available
                      </span>
                    )}
                  </div>

                  {previewProduct.categories &&
                    previewProduct.categories.length > 0 && (
                      <div className="pr-cats">
                        {previewProduct.categories.map((category) => (
                          <span
                            key={category.id || category.name}
                            className="pr-cat"
                          >
                            {category.name}
                          </span>
                        ))}
                      </div>
                    )}

                  {previewProduct.short_description && (
                    <>
                      <div className="adm-sec-eyebrow">Short Description</div>
                      <div
                        className="pr-preview-desc"
                        dangerouslySetInnerHTML={{
                          __html: previewProduct.short_description,
                        }}
                      />
                    </>
                  )}

                  {previewProduct.description && (
                    <>
                      <div className="adm-sec-eyebrow">Full Description</div>
                      <div
                        className="pr-preview-desc"
                        dangerouslySetInnerHTML={{
                          __html: previewProduct.description,
                        }}
                      />
                    </>
                  )}

                  {previewHasPhysical && (
                    <>
                      <div className="adm-sec-eyebrow">Physical Properties</div>
                      <div className="pr-preview-phys">
                        {previewProduct.weight && (
                          <span>Weight: {previewProduct.weight}</span>
                        )}
                        {previewProduct.dimensions &&
                          (previewProduct.dimensions.length ||
                            previewProduct.dimensions.width ||
                            previewProduct.dimensions.height) && (
                            <span>
                              Dimensions:{" "}
                              {previewProduct.dimensions.length || "0"} ×{" "}
                              {previewProduct.dimensions.width || "0"} ×{" "}
                              {previewProduct.dimensions.height || "0"}
                            </span>
                          )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="adm-modal-actions">
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={closePreview}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn--purple"
                  onClick={() =>
                    navigate(`/admin/products/${productId(previewProduct)}/edit`)
                  }
                >
                  Edit Product
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---- delete confirm (legacy delete modal → ConfirmModal) ---- */}
        <ConfirmModal
          open={!!deleteConfirm}
          title="Delete Product"
          body={
            deleteConfirm ? (
              <>
                Are you sure you want to delete "
                <strong>{deleteConfirm.name}</strong>"? This action cannot be
                undone.
              </>
            ) : (
              ""
            )
          }
          confirmLabel="Delete Product"
          danger
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      </div>
    </AdminShell>
  );
};

export default RAdminProducts;
